import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { RatingsBandRefresh } from '@/ui/RatingsBandRefresh';
import { authApi } from './api';
import { useAuthStore } from './store';

/**
 * Wrapper qui hydrate le user au premier mount (GET /auth/me) puis :
 *  - status === 'authenticated' → rend les enfants (+ bandeau si token Lichess expiré)
 *  - status === 'unauthenticated' → redirect /login
 *  - status === 'loading' → écran neutre
 *
 * Stats Hub V2 PR #F : si `user.lichess_oauth_expired === true`, on affiche
 * un bandeau `<RatingsBandRefresh>` au-dessus des enfants pour inviter Léon
 * à reconnecter son compte (sans ça, ses stats Lichess restent vides
 * silencieusement — cf. design plan §8 bug B2).
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const clear = useAuthStore((s) => s.clear);
  const setStatus = useAuthStore((s) => s.setStatus);
  const location = useLocation();

  useEffect(() => {
    if (status !== 'idle') return;
    setStatus('loading');
    authApi
      .fetchMe()
      .then((u) => setUser(u))
      .catch(() => clear());
  }, [status, setUser, clear, setStatus]);

  if (status === 'idle' || status === 'loading') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-ink-900 text-paper-50">
        <p className="text-caption uppercase text-ink-300">Initialisation…</p>
      </main>
    );
  }
  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return (
    <>
      {user?.lichess_oauth_expired === true && (
        <div className="max-w-6xl mx-auto px-4 pt-4">
          <RatingsBandRefresh />
        </div>
      )}
      {children}
    </>
  );
}
